import React, { useState } from 'react';
import { useInvitations } from '../hooks/useInvitations';
import { useSyncStatusIndicator } from '../hooks/useSyncStatusIndicator';
import { Alert, Button, Checkbox, Dialog, DialogBody, DialogFooter, Field, Input } from './ui';

interface InviteMemberModalProps {
  householdId: string;
  householdName: string;
  isOpen: boolean;
  onClose: () => void;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  householdId,
  householdName,
  isOpen,
  onClose,
}) => {
  const [email, setEmail] = useState('');
  const [asChild, setAsChild] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { send, error, clearError } = useInvitations();
  const { isOnline } = useSyncStatusIndicator();

  const handleClose = () => {
    setEmail('');
    setAsChild(false);
    setSuccessMessage('');
    clearError();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setSuccessMessage('');
    try {
      await send(householdId, email.trim(), asChild);
      setSuccessMessage(`Invitation sent to ${email.trim()}${asChild ? ', as a child' : ''}`);
      setEmail('');
      setAsChild(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title={`Invite member to ${householdName}`}
      description="Enter the email address of the person you want to invite."
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <DialogBody>
          {!isOnline && <Alert tone="warning">You must be online to send invitations.</Alert>}
          {successMessage && <Alert tone="success">{successMessage}</Alert>}
          {error && <Alert tone="error">{error}</Alert>}

          <Field label="Email address" htmlFor="inviteEmail">
            <Input
              type="email"
              id="inviteEmail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              required
              disabled={isSubmitting || !isOnline}
            />
          </Field>

          <label className="flex items-start gap-3 text-sm">
            <Checkbox
              checked={asChild}
              onChange={(e) => setAsChild(e.target.checked)}
              disabled={isSubmitting || !isOnline}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-gray-900 dark:text-gray-100">Invite as a child</span>
              <span className="block text-gray-500 dark:text-gray-400">
                They'll see only the chores assigned to them, and can only mark them done.
              </span>
            </span>
          </label>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>Close</Button>
          <Button type="submit" variant="primary" loading={isSubmitting} disabled={!email.trim() || !isOnline}>
            Send invitation
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
};

export default InviteMemberModal;
